// The role-gated app shell: top header + left sidebar + routed content.
// Ported from web-frontend.html. Real role comes from /me + group leadership;
// a dev-only switcher previews other roles.
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useRole } from '../app/RoleContext';
import api, { setOnPaymentRequired } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import AccountMenu from './AccountMenu';
import '../styles/shell.css';

// Sidebar badge for Notifications: unread announcements + pending invites
// (design/021 — invites live in Notifications). One combined request
// (GET /notifications/badge) instead of five. Polls every 60s only while
// the tab is visible; also refreshes on focus and on navigation, throttled
// to at most once per 15s so clicking around doesn't hammer the server.
const BADGE_POLL_MS = 60_000;
const BADGE_MIN_GAP_MS = 15_000;
let lastBadgeFetch = 0;

function useUnreadNotifications() {
  const [count, setCount] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    let alive = true;
    const tick = async (force = false) => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (!force && now - lastBadgeFetch < BADGE_MIN_GAP_MS) return;
      lastBadgeFetch = now;
      try {
        const r = await api.notificationsBadge();
        if (alive) setCount(r?.count || 0);
      } catch { /* keep prior count */ }
    };
    tick();
    const id = setInterval(() => tick(true), BADGE_POLL_MS);
    const onFocus = () => tick();
    const onRefresh = () => tick(true);
    window.addEventListener('badge-refresh', onRefresh);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener('badge-refresh', onRefresh);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [pathname]);
  return count;
}

function NavItem({ to, icon, label, count, hasNew, end }) {
  return (
    <NavLink to={to} end={end} className="sb-item">
      <span className="ic">{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {hasNew && (
        <span style={{
          marginLeft: 'auto', fontSize: '0.625rem', fontWeight: 800, letterSpacing: 0.6,
          color: '#fff', backgroundColor: '#e11d48', border: '1px solid #be123c',
          borderRadius: 999, padding: '1px 6px',
        }}>NEW</span>
      )}
      {count != null && <span className="count" style={{ marginLeft: hasNew ? 6 : 'auto' }}>{count}</span>}
    </NavLink>
  );
}

// design/024 — persisted "last visited" timestamp per browse category
// (V42). Stored server-side under /me/seen so the NEW pill clears cross-
// device. Client caches the map in the hook below.
const SEEN_ROUTES = new Set(['/events', '/groups', '/courses', '/practices']);

/**
 * design/024 — polls the four browse endpoints on shell mount and derives a
 * `hasNew` flag per category (any row with createdAt within the last 7 days).
 * Fires the pill in the sidebar labels. Refetches on route change and every
 * 60s as a backstop.
 */
/**
 * design/024 — asks the server (GET /me/new-flags) whether each category
 * has a row created since max(now - 7d, person_seen.seen_at). One ~200-byte
 * response instead of four browse-list payloads. Poll every 5 minutes as a
 * backstop; clears immediately on route change into a browse page (and
 * fires POST /me/seen/{cat} to persist).
 */
function useHasNewByCategory() {
  const [flags, setFlags] = useState({ events: false, groups: false, courses: false, practices: false });
  const { pathname } = useLocation();

  // Mark the category as seen the moment the user lands on it, so the NEW
  // pill clears immediately. The server write is fire-and-forget.
  useEffect(() => {
    if (SEEN_ROUTES.has(pathname)) {
      const cat = pathname.slice(1);
      setFlags((f) => ({ ...f, [cat]: false }));
      api.markSeen(cat).catch(() => { /* best-effort */ });
    }
  }, [pathname]);

  // Fetch flags on mount + every 5 minutes. Does NOT re-run on pathname
  // changes so navigating around doesn't hammer the endpoint.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const f = await api.newFlags();
        if (!alive || !f) return;
        setFlags({
          events:    !!f.events,
          groups:    !!f.groups,
          courses:   !!f.courses,
          practices: !!f.practices,
        });
      } catch { /* keep prior */ }
    };
    tick();
    const id = setInterval(tick, 5 * 60 * 1000);
    const onFocus = () => tick();
    window.addEventListener('focus', onFocus);
    return () => { alive = false; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, []);

  return flags;
}

// design/029 — open reported posts, for the superadmin's Moderation item.
function useOpenReports(enabled) {
  const [count, setCount] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const tick = () => api.modOpenCount()
      .then((r) => { if (alive) setCount(r?.count || 0); })
      .catch(() => {});
    tick();
    const id = setInterval(tick, 120_000);
    return () => { alive = false; clearInterval(id); };
  }, [enabled, pathname]);
  return count;
}

// design/033 — unseen learner responses, for the Learner Responses item.
function useUnseenResponses(enabled) {
  const [count, setCount] = useState(0);
  const lastRef = useRef(0);
  const { pathname } = useLocation();
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const tick = () => api.responsesCount()
      .then((r) => {
        if (!alive) return;
        const n = r?.unseen || 0;
        // Tell an open Learner Responses page there's something new.
        if (n > lastRef.current) window.dispatchEvent(new Event('responses-new'));
        lastRef.current = n;
        setCount(n);
      })
      .catch(() => {});
    tick();
    const id = setInterval(tick, 120_000);
    // The page fires this after opening a response so the badge drops at once.
    const onChange = () => tick();
    window.addEventListener('responses-changed', onChange);
    return () => { alive = false; clearInterval(id); window.removeEventListener('responses-changed', onChange); };
  }, [enabled, pathname]);
  return count;
}

const ADMIN_ITEMS = [
  { to: '/admin', icon: '▦', label: 'Dashboard', end: true },
  { to: '/admin/members', icon: '👥', label: 'Members' },
  { to: '/admin/moderation', icon: '⚑', label: 'Moderation' },
];

/**
 * Header search input. Client-side controlled — the box tracks its own draft
 * state and pushes to `/search?q=…` on Enter (or when the user clicks the
 * icon). Prefilled with the current `?q=` when on the search page so the
 * box stays consistent as the user types.
 */
function HeaderSearch() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const [q, setQ] = useState('');
  useEffect(() => {
    if (pathname === '/search') setQ(params.get('q') || '');
  }, [pathname, params]);
  const submit = () => {
    const t = q.trim();
    if (!t) return;
    navigate(`/search?q=${encodeURIComponent(t)}`);
  };
  const clear = () => {
    setQ('');
    // If the user was viewing search results, take them home when they
    // clear — otherwise the input is empty but the results view lingers.
    if (pathname === '/search') navigate('/');
  };
  return (
    <div className="header-search">
      <span className="ic" onClick={submit} style={{ cursor: 'pointer' }}>🔍</span>
      <input
        placeholder="Search groups, events, courses, practices…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
      />
      {q ? (
        <button
          type="button"
          onClick={clear}
          aria-label="Clear search"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--muted, #6b7280)', fontSize: 16, padding: '0 6px', lineHeight: 1,
          }}
        >
          ✕
        </button>
      ) : null}
    </div>
  );
}

/**
 * design/025 — when the server's paywall answers 402 (NO_SUBSCRIPTION), send
 * the person to the Billing page. The gate lives on the server
 * (BILLING_GATE_ENABLED); with it off, 402 never happens.
 */
// Pages a person without a subscription may still open when the paywall is on.
const PAYWALL_OPEN_PATHS = ['/billing', '/profile', '/terms', '/privacy'];

function usePaywallRedirect() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { me } = useAuth();
  const pathRef = useRef(pathname);
  pathRef.current = pathname;
  useEffect(() => {
    setOnPaymentRequired(() => {
      if (pathRef.current !== '/billing') navigate('/billing', { replace: true });
    });
    return () => setOnPaymentRequired(null);
  }, [navigate]);

  // Proactive: with the server paywall on (/me.subscription.paywallOn), send
  // anyone without access to Billing right away instead of waiting for the
  // first 402. Staff have hasAccess=true so they're never redirected.
  const sub = me?.subscription;
  useEffect(() => {
    if (!sub || !sub.paywallOn || sub.hasAccess) return;
    if (PAYWALL_OPEN_PATHS.includes(pathname)) return;
    navigate('/billing', { replace: true });
  }, [sub?.paywallOn, sub?.hasAccess, pathname, navigate]);
}

export default function AppShell() {
  const { effectiveRole, canSeeAdmin, canManageGroups, canCreateEvents, canSeeContentLibrary, isEventLeader, canPublish } = useRole();
  const unread = useUnreadNotifications();
  const newFlags = useHasNewByCategory();
  const openReports = useOpenReports(!!canSeeAdmin);
  const unseenResponses = useUnseenResponses(!!canPublish);
  usePaywallRedirect();

  return (
    <>
      <header className="app-header">
        <NavLink to="/" className="brand" aria-label="Compassion Practice — Home"
          style={{ textDecoration: 'none', cursor: 'pointer' }}>
          <img className="logo-img" src="/logo.png" alt="" />
          <div>Compassion Practice</div>
        </NavLink>

        <HeaderSearch />

        <div className="header-right">
          <AccountMenu />
        </div>
      </header>

      <div className="app-shell">
        <aside className="sidebar">
          {/* Nav re-org per design/020: My Invites moved inline to Home,
              Library retired and split into Courses+Practices,
              My Groups/My Events renamed to plain Groups/Events (they were
              always the catalogs — only the label was "My"). */}
          <NavItem to="/" end icon="🏠" label="Home" />
          <NavItem to="/notifications" icon="🔔" label="Notifications" count={unread || null} />
          {/* design/024 order matches Home: Events → Groups → Courses → Practices. */}
          <NavItem to="/events" end icon="📅" label="Events" hasNew={newFlags.events} />
          <NavItem to="/groups" end icon="👥" label="Groups" hasNew={newFlags.groups} />
          <NavItem to="/courses" icon="📚" label="Courses" hasNew={newFlags.courses} />
          <NavItem to="/practices" icon="🧘" label="Practices" hasNew={newFlags.practices} />

          {/* Manager tools (design/021) — one unified section for Publishers
              and Event Leaders. Each nav item is gated on the specific
              capability so a pure Event Leader (no Publisher) sees just
              "Manage events" while a Publisher sees the full set. */}
          {(canManageGroups || canCreateEvents) && (
            <>
              <div className="sb-section">Manager tools</div>
              {canCreateEvents && <NavItem to="/leader/events"       icon="📅" label="Events" />}
              {canManageGroups && <NavItem to="/leader/group"        icon="👥" label="Groups" />}
              {canPublish && <NavItem to="/admin/courses"       icon="📚" label="Courses" />}
              {canPublish && <NavItem to="/admin/practices"     icon="🧘" label="Practices" />}
              {(canManageGroups || canCreateEvents) && <NavItem to="/admin/learn-members" icon="🧑‍🎓" label="Members" />}
              {canPublish && <NavItem to="/learner-responses" icon="💬" label="Learner Responses" count={unseenResponses || null} />}
            </>
          )}

          {/* Account (Profile/Billing) lives in the username dropdown top-right
              (AccountMenu) — hidden from the sidebar for now. */}

          {/* Admin block: only superadmins see it at all. */}
          {canSeeAdmin && (
            <div className="admin-block">
              <div className="sb-section">🔓 Admin tools</div>
              {ADMIN_ITEMS.map((it) => (
                <NavItem key={it.to} to={it.to} icon={it.icon} label={it.label} end={it.end}
                  count={it.to === '/admin/moderation' && openReports ? openReports : null} />
              ))}
            </div>
          )}
        </aside>

        <main>
          <Outlet context={{ effectiveRole }} />
        </main>
      </div>
    </>
  );
}
